import { observer } from "mobx-react";
import { FunctionComponent, useCallback, useMemo, useState } from "react";

import NotificationsFilters from "@ui/components/notifications/NotificationsFilters/NotificationsFilters";
import NotificationsHeader from "@ui/components/notifications/NotificationsHeader/NotificationsHeader";
import NotificationsList from "@ui/components/notifications/NotificationsList/NotificationsList";
import { useComponentConfig } from "@ui/context/component-config-context";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import { useAuth } from "@voxmedia/duet-context/context/auth-context";
import {
  AnyNotification,
  NotificationsFilter,
  useNotificationsStore,
  NotificationType,
  useOptionalNotificationsDrawerContext,
} from "@voxmedia/duet-context/context/notifications-context";

import * as styles from "./NotificationsContent.css";

const eventTypes = [
  { notificationType: NotificationType.CoralRec, eventType: "rec" },
  { notificationType: NotificationType.CoralReply, eventType: "comment" },
  { notificationType: NotificationType.CycloneFanPostRec, eventType: "rec" },
  {
    notificationType: NotificationType.CycloneFanPostReply,
    eventType: "comment",
  },
  { notificationType: NotificationType.CycloneReply, eventType: "comment" },
  { notificationType: NotificationType.CycloneReplyRec, eventType: "rec" },
];

export enum ContentMode {
  Modal = "Modal",
  Page = "Page",
}

interface NotificationsContentProps {
  showGoToNotificationsPageLink?: boolean;
  onCloseNotifications?: () => void;
  initialFilter?: "all" | "unread";
  onFilterChange?: (filter: "all" | "unread") => void;
  mode: ContentMode;
}

const NotificationsContent: FunctionComponent<NotificationsContentProps> =
  observer(
    ({
      showGoToNotificationsPageLink = true,
      onCloseNotifications,
      initialFilter = "all",
      onFilterChange,
      mode,
    }) => {
      const { RootStore } = useNotificationsStore();
      const [filter, setFilter] = useState<"all" | "unread">(initialFilter);
      const { userLoading } = useAuth();
      const analytics = useAnalytics();
      const notificationsDrawer = useOptionalNotificationsDrawerContext();
      const { filtersVariant } = useComponentConfig("notificationsList");
      // The drawer surface (currently Verge-only) renders its own close
      // affordance over the top-right corner, so move the actions menu inline
      // beside the title to avoid overlap.
      const inlineHeaderActions =
        !!notificationsDrawer && mode === ContentMode.Modal;

      const notificationsList =
        filter === "unread"
          ? RootStore?.unreadNotificationsStore
          : RootStore?.allNotificationsStore;
      const notifications = notificationsList?.visibleNotifications;
      const notificationsHasNext = notificationsList?.hasNext;
      const notificationsLoading = notificationsList?.loading;

      const allowPagination = !showGoToNotificationsPageLink; // if we're showing the go to notifications page link, we don't allow pagination

      const handleNotificationItemClick = useCallback(
        async (notification: AnyNotification) => {
          await RootStore?.notificationsStore.markRead({
            ids: [notification.id],
            // don't update front-end store if we're on the Unread filter,
            // otherwise it'll immediately disappear from the list
            updateStore: filter !== "unread",
          });

          const type = eventTypes.find(
            (et) => et.notificationType === notification.type,
          );

          analytics?.emit({
            event: "notification_reply",
            notification_reply: {
              type: type?.eventType,
            },
          });
        },
        [RootStore?.notificationsStore, filter, analytics],
      );

      const paginateNotifications = useCallback(async () => {
        if (!notificationsHasNext || notificationsLoading || !allowPagination) {
          return;
        }

        if (filter === "unread") {
          await RootStore?.unreadNotificationsStore.loadNotificationsForUser({
            cursor: RootStore.unreadNotificationsStore.cursor ?? undefined,
            filter: NotificationsFilter.Unread,
          });
        } else {
          await RootStore?.allNotificationsStore.loadNotificationsForUser({
            cursor: RootStore.allNotificationsStore.cursor ?? undefined,
          });
        }
      }, [
        notificationsHasNext,
        notificationsLoading,
        RootStore,
        filter,
        allowPagination,
      ]);

      const loadMoreText = useMemo(() => {
        if (allowPagination) {
          return "See previous notifications";
        } else {
          return `See all${filter === "unread" ? " unread" : ""} notifications`;
        }
      }, [allowPagination, filter]);

      const onLoadMoreClick = useCallback(() => {
        if (allowPagination) {
          void paginateNotifications();
        } else {
          analytics?.emit({
            event: "notification_page_click",
            notification_page_click: {
              click_text: loadMoreText,
            },
          });
          window.location.href = `/notifications?filter=${filter}`;
        }
      }, [
        allowPagination,
        paginateNotifications,
        filter,
        analytics,
        loadMoreText,
      ]);

      const onFilterClick = useCallback(
        (filter: "all" | "unread") => {
          setFilter(filter);
          onFilterChange?.(filter);
        },
        [onFilterChange],
      );

      const showLoader = Boolean(
        notificationsLoading ||
        userLoading ||
        RootStore?.userStore.viewerLoading,
      );

      return (
        <div className={styles.container[mode]}>
          <NotificationsHeader
            showGoToNotificationsPageLink={showGoToNotificationsPageLink}
            onCloseNotifications={
              mode === ContentMode.Modal ? onCloseNotifications : undefined
            }
            tabIndexes={{
              threeDots:
                mode === ContentMode.Page || inlineHeaderActions ? 0 : -1,
            }}
            inlineActions={inlineHeaderActions}
          />
          <NotificationsFilters
            filter={filter}
            onFilterClick={onFilterClick}
            variant={filtersVariant}
          />
          {RootStore ? (
            <>
              <NotificationsList
                notifications={notifications ?? []}
                showLoader={showLoader}
                onLoadMoreClick={onLoadMoreClick}
                loadMoreText={loadMoreText}
                onNotificationItemClick={handleNotificationItemClick}
                notificationsHasNext={!!notificationsHasNext}
              />
            </>
          ) : (
            <div className={styles.notificationsSpinner} />
          )}
        </div>
      );
    },
  );

export default NotificationsContent;
